import React, { useEffect, useRef, useState } from 'react';
import {
  Menu,
  Form,
  Button,
  Space,
  Select,
  Input,
  Divider,
  Dropdown,
  message,
  Spin,
  Modal,
  Tooltip,
  InputRef,
} from 'antd';
import AttributeList from '../compontents/attribute-list';
import { Add, Close, More, EmptyFileColor } from 'patternx-svg';
import {
  addPreProp,
  copyPreProp,
  deletePreProp,
  getPrePropList,
  getPrePropInfo,
  publishPreProp,
  updatePreProp,
} from '../../../services/props';
import { pick } from 'lodash';
import classNames from 'classnames';
import './index.less';
import PtxEmpty from '../../../components/ptx-empty';

let updated: boolean = false;
const RulePreProps: React.FC<{}> = () => {
  const [preProps, setPreProps] = useState<any[]>([]);
  const [selectedKey, setSelectedKey] = useState<any>('');
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const inputRef = useRef<InputRef>(null);
  const prePropName = Form.useWatch('name', form);

  const refresh = () => {
    if (selectedKey?.isNew) {
      setPreProps((prevState) => prevState.filter((item) => !item?.isNew));
    } else if (updated) {
      const { id, name, conditionRelation, conditions } = selectedKey;
      form.setFieldsValue({
        id,
        conditionRelation,
        name,
        conditionDTOList: conditions.map((item: any) =>
          pick(item, ['id', 'compareKey', 'compareType', 'compareValue']),
        ),
      });
    }
  };

  // 取消
  const handleCancel = (cancelCallback?: any) => {
    if (updated) {
      Modal.confirm({
        title: `确认放弃本次${selectedKey?.isNew ? '新增' : '修改'}吗？`,
        content: selectedKey?.isNew ? '放弃后将不会新增此属性。' : '放弃后将不保留未保存的内容。',
        okText: '放弃',
        cancelText: '关闭',
        onOk() {
          updated = false;
          refresh();
          setTimeout(() => {
            cancelCallback?.();
          }, 100);
        },
        onCancel() {},
      });
    } else {
      refresh();
      setTimeout(() => {
        cancelCallback?.();
      }, 100);
    }

    return updated;
  };

  const handleAdd = () => {
    if (preProps.find((item) => item.isNew)) {
      Modal.warning({
        title: '有未保存的新属性，请取消或保存后再新增',
        content: '',
      });
      return;
    }
    handleCancel(() => {
      const newPreProp = { id: 'new', name: '未命名属性', isNew: true };
      setPreProps((prev) => [...prev, newPreProp]);
      setSelectedKey(newPreProp);
      inputRef.current!.focus({
        cursor: 'all',
      });
    });
  };

  const cancelAdd = () => {
    handleCancel(() => {
      setSelectedKey(preProps?.[0]);
    });
  };

  const handleChecked = async (record: any, checked: boolean) => {
    const { success } = await publishPreProp({ prePropId: record.id, published: checked });
    if (success) {
      if (checked) {
        message.success('发布成功');
      }
      setPreProps(
        preProps.map((item) =>
          item.id === record.id ? { ...item, checked, published: checked } : item,
        ),
      );
      if (record.id === selectedKey?.id) {
        setSelectedKey((prev: any) => ({ ...prev, published: checked }));
      }
    }
  };

  const handleSelect = (record: any) => {
    handleCancel(() => setSelectedKey(record));
  };

  // 加载预设对象列表
  const loadPreProp = async () => {
    setLoading(true);
    const { success, data } = await getPrePropList();
    let newPreProps = [];
    if (success && data?.details) {
      newPreProps = data.details?.map((item: any) => ({
        ...item,
        checked: item.published,
      }));
      setPreProps(newPreProps);
    }
    setLoading(false);

    return newPreProps;
  };

  // 获取预设对象详情
  const getPropInfo = async (obj: any) => {
    setLoading(true);

    let fields: any = {};
    if (obj.isNew) {
      fields = {
        id: '',
        conditionRelation: '所有',
        name: obj.name,
        conditionDTOList: [{ compareKey: '', compareType: '包含', compareValue: '' }],
      };
    } else {
      const { success, data } = await getPrePropInfo(obj.id);
      if (!success || !data?.details) {
        return;
      }
      const { id, name, conditionRelation, conditions } = data?.details;
      fields = {
        id,
        conditionRelation: conditionRelation || '所有',
        name,
        conditionDTOList: conditions.map((item: any) =>
          pick(item, ['id', 'compareKey', 'compareType', 'compareValue']),
        ),
      };
    }

    form.setFieldsValue(fields);
    setLoading(false);

    return fields;
  };

  const addProp = async (values: any) => {
    setLoading(true);
    values?.conditionDTOList?.forEach((c: any) => {
      c.compareKey = values?.name || '';
    });
    const { success, data } = await addPreProp(values);
    if (success) {
      message.success('保存成功');
      const res = await loadPreProp();
      if (data?.details?.id) {
        setSelectedKey(res.find((item: any) => item.id === data.details.id));
      }
    }
    setLoading(false);
  };

  const updateProp = async (values: any) => {
    setLoading(true);
    values?.conditionDTOList?.forEach((c: any) => {
      c.compareKey = values?.name || '';
    });
    const { success } = await updatePreProp(values).catch(() => setLoading(false));
    if (success) {
      message.success('保存成功');
      const { name } = (await getPropInfo({ id: selectedKey?.id })) || {};
      setSelectedKey((prev: any) => ({ ...prev, name }));
      setPreProps((prevState) =>
        prevState.map((item) => (item.id === values.id ? { ...item, name } : item)),
      );
    } else {
      setLoading(false);
    }
  };

  const handleFinish = async (values: any) => {
    updated = false;
    if (values.id) {
      await updateProp(values);
    } else {
      await addProp(values);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    const { errorFields } = errorInfo || {};
    if (errorFields.some((error: any) => error.name[0] === 'conditionDTOList')) {
      message.error('有内容未填写');
    }
  };

  const onFieldsChange = () => {
    updated = true;
  };

  useEffect(() => {
    loadPreProp().then((res) => {
      if (res?.length > 0) {
        setSelectedKey(res[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedKey?.id) {
      return;
    }
    getPropInfo(selectedKey);
  }, [selectedKey?.id]);

  const handleSave = () => {
    form.submit();
  };

  const refreshPreProp = async () => {
    const res = await loadPreProp();
    if (!res.find((item: any) => item.id === selectedKey?.id)) {
      setSelectedKey(res?.[0]);
    }
  };

  const deleteProp = async (objId: string) => {
    Modal.confirm({
      title: `确认删除属性吗？`,
      content: `删除后所有规则将不可使用此属性，已应用此属性的规则可能出现缺失，请慎重操作。`,
      okText: '删除',
      okButtonProps: {
        type: 'primary',
        danger: true,
      },
      cancelText: '再想想',
      onOk: async () => {
        const { success } = await deletePreProp(objId);
        if (success) {
          await refreshPreProp();
        }
      },
      onCancel() {},
    });
  };

  const copyProp = async (objId: string) => {
    handleCancel(async () => {
      setLoading(true);
      const { success, data } = await copyPreProp(objId);
      if (success) {
        await refreshPreProp();
        setSelectedKey({ id: data?.details?.id, name: data?.details?.name });
      }
      setLoading(false);
    });
  };

  const dropdownOverlay = (item: any) => {
    return (
      <Menu style={{ width: 80 }}>
        <Menu.Item key="copy" onClick={() => copyProp(item.id)}>
          复制
        </Menu.Item>
        {!item?.published && (
          <Menu.Item key="delete">
            <div className="color-error" onClick={() => deleteProp(item.id)}>
              删除
            </div>
          </Menu.Item>
        )}
      </Menu>
    );
  };

  const compareTypes = [
    { label: '包含', value: '包含' },
    { label: '不包含', value: '不包含' },
    { label: '等于', value: '等于' },
  ];

  return (
    <Spin spinning={loading}>
      <div className="props-wrap">
        <div className="page-header">属性管理</div>
        {preProps?.length > 0 ? (
          <div className="page-content">
            <div className="page-content-left">
              <div className="attribute-header">
                <div className="attribute-header-title">预设属性</div>
                <Tooltip placement="top" title="新增属性">
                  <Add className="attribute-header-icon" onClick={handleAdd} />
                </Tooltip>
              </div>
              <AttributeList
                data={preProps}
                selectedKey={selectedKey?.id}
                dropdownOverlay={dropdownOverlay}
                onChecked={handleChecked}
                onSelect={handleSelect}
              />
            </div>
            <div className="page-content-right">
              <div className="preProp-form-header">
                <span>{prePropName || selectedKey?.name}</span>
                {selectedKey?.isNew ? (
                  <Space size={12}>
                    <Button key="cancelBtn" onClick={cancelAdd}>
                      取消
                    </Button>
                    <Button key="addSaveBtn" type="primary" onClick={handleSave}>
                      保存
                    </Button>
                  </Space>
                ) : (
                  <Space size={16} align="end">
                    <Button key="editSaveBtn" type="primary" onClick={handleSave}>
                      保存
                    </Button>
                    <Dropdown trigger={['click']} overlay={() => dropdownOverlay(selectedKey)}>
                      <More className="operate-more" />
                    </Dropdown>
                  </Space>
                )}
              </div>
              <Form
                form={form}
                name="preProp-form-nest-item"
                className="preProp-form-nest-item"
                onFinish={handleFinish}
                onFinishFailed={onFinishFailed}
                onFieldsChange={onFieldsChange}
                autoComplete="off"
              >
                <Form.Item name="id" hidden>
                  <Input placeholder="id" />
                </Form.Item>
                <div className="preProp-form-label">属性名称</div>
                <Form.Item name={'name'} rules={[{ required: true, message: '请输入名称' }]}>
                  <Input placeholder="请输入名称" maxLength={255} ref={inputRef} />
                </Form.Item>

                <Divider />

                <div style={{ marginBottom: 18 }}>
                  <Space>
                    <span>满足以下</span>
                    <Form.Item name="conditionRelation" className="preProp-form-no-margin">
                      <Select size="small" style={{ width: 72 }}>
                        <Select.Option value="所有">所有</Select.Option>
                        <Select.Option value="任一">任一</Select.Option>
                      </Select>
                    </Form.Item>
                    <span>条件：</span>
                  </Space>
                </div>

                <Form.List name="conditionDTOList">
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key}>
                          <Form.Item name={[name, 'id']} hidden>
                            <Input placeholder="id" />
                          </Form.Item>
                          <Space
                            size={12}
                            className="preProp-form-conditions"
                            style={{ display: 'flex', marginBottom: 16 }}
                            align="end"
                          >
                            {/* <Form.Item
                              {...restField}
                              className="preProp-form-no-margin"
                              name={[name, 'compareKey']}
                              // rules={[{ required: true, message: '' }]}
                            >
                              // <Select options={compareKeys} />
                              <Input readOnly bordered={false} />
                            </Form.Item> */}
                            <span className="preProp-form-item-name">属性名称</span>
                            <Form.Item
                              {...restField}
                              className="preProp-form-no-margin"
                              name={[name, 'compareType']}
                              rules={[{ required: true, message: '' }]}
                            >
                              <Select options={compareTypes} />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              className="preProp-form-no-margin"
                              name={[name, 'compareValue']}
                              rules={[{ required: true, message: '' }]}
                            >
                              <Input placeholder="多个内容用逗号隔开，满足其一即可" />
                            </Form.Item>
                            <Close
                              className={classNames('close-icon', {
                                'close-icon-hidden': key === 0,
                              })}
                              onClick={() => remove(name)}
                            />
                          </Space>
                        </div>
                      ))}
                      <Button
                        className="preProp-form-add-btn"
                        type="link"
                        onClick={() =>
                          add({ compareKey: '', compareType: '包含', compareValue: '' })
                        }
                        icon={<Add size={20} />}
                      >
                        添加条件
                      </Button>
                    </>
                  )}
                </Form.List>
              </Form>
            </div>
          </div>
        ) : (
          <PtxEmpty
            isEmpty={true}
            icon={<EmptyFileColor />}
            describe="没有属性"
            subDescribe="管理具有行业共识的通用属性（例如高度、宽度等），可以在规则库内被引用形成预置审查规则。"
            btnRender={() => (
              <Button type="primary" onClick={handleAdd}>
                新增属性
              </Button>
            )}
          />
        )}
      </div>
    </Spin>
  );
};

export default RulePreProps;
